"use client"

import { notFound } from "next/navigation"
import { use } from "react"

import { useTspRaceWithRuns } from "@/lib/apps/tsp/hooks/use-race-queries"

import ErrorBanner from "@/components/nango/common/components/error-banner"
import RunTable from "@/components/nango/tsp/run-table"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import type { Result } from "@/nango/types/apps.tsp/race/Race"

export default function TspPublicRacePage({
  params,
}: {
  params: Promise<{ slug: string }>
}) {
  const { slug } = use(params)
  const { data: race, error, isLoading } = useTspRaceWithRuns(slug)

  if (!isLoading && !error && !race) {
    notFound()
  }

  const QExists = race?.runs.some((run) => run.category === "Q")
  const F1Exists = race?.runs.some((run) => run.category === "F1")
  const F2Exists = race?.runs.some((run) => run.category === "F2")
  const FExists = race?.runs.some((run) => run.category === "F")

  function resultsFor(category: string): Result[] | undefined {
    return race?.runs.find((run) => run.category === category)?.results
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-6 py-8">
      {error ? (
        <ErrorBanner
          errorMessage={error.message}
          fixes={["Refresh the page"]}
        />
      ) : (
        <>
          <div className="flex flex-col space-y-4">
            <h1 className="text-primary text-2xl font-extrabold tracking-tight lg:text-4xl">
              {race?.place || <Skeleton className="h-10 w-52" />}
            </h1>
            <h2 className="text-muted-foreground max-w-lg text-lg font-light lg:text-xl">
              {race?.gender ? (
                <>
                  {race.gender === "M" ? "Men's" : "Women's"} {race.circuit}{" "}
                  {new Date(race.date).toLocaleDateString()}
                </>
              ) : (
                <Skeleton className="h-6 w-32" />
              )}
            </h2>
          </div>

          <div className="flex w-full flex-row gap-2 pt-8">
            {!F1Exists && !F2Exists && !FExists ? (
              <RunTable
                showProMode={false}
                results={resultsFor("Q")}
                viewMode={"Default"}
                setViewMode={() => null}
              />
            ) : (
              <Tabs defaultValue="leaderboard" className="w-full">
                <TabsList className="flex h-fit w-full flex-col sm:flex-row">
                  {QExists && (
                    <TabsTrigger value="Q" className="w-full">
                      Qualification
                    </TabsTrigger>
                  )}
                  {F1Exists && (
                    <TabsTrigger value="F1" className="w-full">
                      Final
                    </TabsTrigger>
                  )}
                  {FExists && !F2Exists && (
                    <TabsTrigger value="F" className="w-full">
                      Super Final
                    </TabsTrigger>
                  )}
                  {F2Exists && (
                    <TabsTrigger value="F2" className="w-full">
                      Super Final
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="leaderboard" className="w-full">
                    Leaderboard
                  </TabsTrigger>
                </TabsList>
                {QExists && (
                  <TabsContent value="Q">
                    <RunTable
                      viewMode={"Default"}
                      setViewMode={() => null}
                      showProMode={false}
                      results={resultsFor("Q")}
                    />
                  </TabsContent>
                )}
                {F1Exists && (
                  <TabsContent value="F1">
                    <RunTable
                      viewMode={"Default"}
                      setViewMode={() => null}
                      showProMode={false}
                      results={resultsFor("F1")}
                    />
                  </TabsContent>
                )}
                {FExists && !F2Exists && (
                  <TabsContent value="F">
                    <RunTable
                      viewMode={"Default"}
                      setViewMode={() => null}
                      showProMode={false}
                      results={resultsFor("F")}
                    />
                  </TabsContent>
                )}
                {F2Exists && (
                  <TabsContent value="F2">
                    <RunTable
                      viewMode={"Default"}
                      setViewMode={() => null}
                      showProMode={false}
                      results={resultsFor("F2")}
                    />
                  </TabsContent>
                )}
                <TabsContent value="leaderboard">
                  <RunTable
                    viewMode={"Default"}
                    setViewMode={() => null}
                    showProMode={false}
                    filter={false}
                    results={race?.leaderboard}
                    isLeaderboard
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </>
      )}
    </div>
  )
}
